import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { notificationService, Notification, NotificationSettings } from '../services/notificationService';
import { firebaseService } from '../services/firebaseService';
import { soundService, SoundSettings } from '../services/soundService';
import { useAuth } from '../hooks/use-auth';
import { useToast } from '../hooks/use-toast';

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  isLoading: boolean;
  settings: NotificationSettings | null;
  soundSettings: SoundSettings;
  markAsRead: (id: number) => void;
  markAllAsRead: () => void;
  deleteNotification: (id: number) => void;
  refreshNotifications: () => void;
  updateSettings: (settings: NotificationSettings) => void;
  updateSoundSettings: (settings: Partial<SoundSettings>) => void;
  testSound: (type?: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [unreadCount, setUnreadCount] = useState(0);
  const [soundSettings, setSoundSettings] = useState<SoundSettings>(soundService.getSettings());
  const [previousNotificationCount, setPreviousNotificationCount] = useState(0);

  // Fetch notifications
  const { data: notificationsData, isLoading, refetch } = useQuery({
    queryKey: ['notifications'],
    queryFn: () => notificationService.getUserNotifications({ limit: 50 }),
    enabled: !!user,
    refetchInterval: 30000, // Refetch every 30 seconds
    retry: (failureCount, error: any) => {
      if (error.response?.status === 401) return false;
      return failureCount < 3;
    },
  });

  // Fetch notification settings
  const { data: settings } = useQuery({
    queryKey: ['notification-settings'],
    queryFn: () => notificationService.getNotificationSettings(),
    enabled: !!user,
    retry: (failureCount, error: any) => {
      if (error.response?.status === 401) return false;
      return failureCount < 3;
    },
  });

  // Mark as read mutation
  const markAsReadMutation = useMutation({
    mutationFn: (id: number) => notificationService.markAsRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "Failed to mark notification as read",
        variant: "destructive"
      });
    }
  });

  // Mark all as read mutation
  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationService.markAllAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      setUnreadCount(0);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "Failed to mark all notifications as read",
        variant: "destructive"
      });
    }
  });

  // Delete notification mutation
  const deleteNotificationMutation = useMutation({
    mutationFn: (id: number) => notificationService.deleteNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "Failed to delete notification",
        variant: "destructive"
      });
    }
  });

  // Update settings mutation
  const updateSettingsMutation = useMutation({
    mutationFn: (settings: NotificationSettings) => notificationService.updateNotificationSettings(settings),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-settings'] });
      toast({
        title: "Success",
        description: "Notification settings updated successfully"
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: "Failed to update notification settings",
        variant: "destructive"
      });
    }
  });

  // Update unread count when notifications change and play sound for new notifications
  useEffect(() => {
    if (notificationsData?.data) {
      const unread = notificationsData.data.filter(n => !n.is_read).length;
      const totalNotifications = notificationsData.data.length;
      
      // Play sound if there are new notifications (more total notifications than before)
      if (previousNotificationCount > 0 && totalNotifications > previousNotificationCount) {
        console.log('🔔 New notifications detected, playing sound:', {
          previous: previousNotificationCount,
          current: totalNotifications,
          new: totalNotifications - previousNotificationCount
        });
        soundService.playNotificationSound();
      } else {
        console.log('🔔 No new notifications to play sound for:', {
          previous: previousNotificationCount,
          current: totalNotifications
        });
      }
      
      setUnreadCount(unread);
      setPreviousNotificationCount(totalNotifications);
    }
  }, [notificationsData, previousNotificationCount]);

  // Initialize Firebase when user is authenticated
  useEffect(() => {
    if (user && firebaseService.isSupported()) {
      console.log('🔔 Initializing Firebase for user:', user.id);
      
      firebaseService.initialize().then((token) => {
        if (token) {
          console.log('🔔 Firebase initialized successfully');
        }
      });

      // Listen for foreground messages
      const unsubscribe = firebaseService.onMessage((payload) => {
        console.log('🔔 Received foreground message:', payload);
        
        // Play notification sound
        console.log('🔔 Playing sound for Firebase message');
        soundService.playNotificationSound();
        
        // Refresh notifications when new message arrives
        refetch();
        
        // Show toast notification
        if (payload.notification) {
          toast({
            title: payload.notification.title || "New Notification",
            description: payload.notification.body || "",
          });
        }
      });

      return unsubscribe || undefined;
    }
  }, [user, refetch, toast]);

  // Handlers
  const handleMarkAsRead = useCallback((id: number) => {
    markAsReadMutation.mutate(id);
  }, [markAsReadMutation]);

  const handleMarkAllAsRead = useCallback(() => {
    markAllAsReadMutation.mutate();
  }, [markAllAsReadMutation]);

  const handleDeleteNotification = useCallback((id: number) => {
    deleteNotificationMutation.mutate(id);
  }, [deleteNotificationMutation]);

  const handleRefreshNotifications = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleUpdateSettings = useCallback((newSettings: NotificationSettings) => {
    updateSettingsMutation.mutate(newSettings);
  }, [updateSettingsMutation]);

  const handleUpdateSoundSettings = useCallback((newSettings: Partial<SoundSettings>) => {
    soundService.updateSettings(newSettings);
    setSoundSettings(soundService.getSettings());
  }, []);

  const handleTestSound = useCallback((type?: string) => {
    soundService.testSound(type);
  }, []);

  const value: NotificationContextType = {
    notifications: notificationsData?.data || [],
    unreadCount,
    isLoading,
    settings: settings || null,
    soundSettings,
    markAsRead: handleMarkAsRead,
    markAllAsRead: handleMarkAllAsRead,
    deleteNotification: handleDeleteNotification,
    refreshNotifications: handleRefreshNotifications,
    updateSettings: handleUpdateSettings,
    updateSoundSettings: handleUpdateSoundSettings,
    testSound: handleTestSound,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
